import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import seoData from '../seo/home.json';

const PhoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);

const StarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const ChevyLogo = () => (
  <svg viewBox="0 0 120 44" width="28" height="10" fill="currentColor">
    <polygon points="0,22 28,2 42,2 22,22 42,42 28,42"/>
    <polygon points="78,2 92,2 120,22 92,42 78,42 98,22"/>
    <rect x="32" y="8" width="56" height="28" rx="2"/>
  </svg>
);

const CadillacLogo = () => (
  <svg viewBox="0 0 32 42" width="18" height="24" fill="currentColor">
    <path d="M16,0 L32,8 L32,30 L16,42 L0,30 L0,8 Z"/>
    <path d="M16,4 L28,10 L28,28 L16,38 L4,28 L4,10 Z" fill="var(--bg-card)"/>
    <rect x="14.5" y="7" width="3" height="28" rx="1" fill="currentColor" opacity="0.6"/>
    <rect x="6" y="19.5" width="20" height="3" rx="1" fill="currentColor" opacity="0.6"/>
  </svg>
);

export default function Home() {
  const services = [
    { icon: '\u2708', title: 'Airport Transfers', desc: 'Seamless pickups and drop-offs at LAX, Burbank, and all major airports. Flight tracking included.' },
    { icon: '\uD83C\uDFE2', title: 'Corporate Travel', desc: 'Executive transportation for meetings, conferences, and business events throughout Los Angeles.' },
    { icon: '\uD83D\uDC8D', title: 'Weddings & Events', desc: 'Make your special day unforgettable with our luxury fleet. Multiple vehicles available for groups.' },
    { icon: '\uD83C\uDF19', title: 'Nightlife & Tours', desc: 'Experience LA nightlife in style. Club runs, wine tours, and sightseeing with a professional chauffeur.' },
  ];

  const testimonials = [
    { text: 'Absolutely the best car service in LA. The Suburban was immaculate and the driver was incredibly professional. Will use again for every airport run.', author: 'Michael R.', role: 'Corporate Client' },
    { text: 'We booked two Suburbans for our wedding party and the experience was flawless. On time, elegant, and the attention to detail was remarkable.', author: 'Sarah & David K.', role: 'Wedding Couple' },
    { text: 'The Cadillac Lyriq was stunning. Quiet, smooth ride and my driver knew every back route in LA. Premium service all the way.', author: 'Jennifer L.', role: 'Regular Client' },
  ];

  return (
    <>
      <SEOHead seo={seoData} />

      {/* ===== HERO ===== */}
      <section className="hero">
        <div className="hero-bg" />
        <div className="hero-grid" />
        <div className="hero-accent" />
        <div className="hero-line-v" />
        <div className="hero-line-v" />
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="hero-badge-dot" />
              Premium Black Car Service
            </div>
            <h1>
              Luxury<br />
              Transportation<br />
              in <em>Los Angeles</em>
            </h1>
            <p className="hero-desc">
              Experience elite ground transportation with our fleet of premium black vehicles.
              Professional chauffeurs, impeccable service, available around the clock.
            </p>
            <div className="hero-actions">
              <Link to="/contact" className="btn-primary">
                Book Your Ride <ArrowIcon />
              </Link>
              <a href="tel:8189154566" className="btn-outline">
                <PhoneIcon /> Call Now
              </a>
            </div>
            <div className="hero-phones">
              <div className="hero-phone-item">
                <span className="hero-phone-label">Primary Line</span>
                <a href="tel:8189154566" className="hero-phone-number">(818) 915-4566</a>
              </div>
              <div className="hero-phone-item">
                <span className="hero-phone-label">Secondary Line</span>
                <a href="tel:8183559929" className="hero-phone-number">(818) 355-9929</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FLEET PREVIEW ===== */}
      <section className="section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="section-label">Our Fleet</div>
          <h2 className="section-title">Premium Black Vehicles</h2>
          <p className="section-subtitle">
            Meticulously maintained, always spotless. Our fleet represents the pinnacle of luxury ground transportation.
          </p>
          <div className="fleet-grid">
            <div className="fleet-card">
              <div className="fleet-card-image">
                <img
                  src="/images/suburban.png"
                  alt="Black Chevrolet Suburban luxury SUV"
                  loading="lazy"
                />
                <div className="fleet-tag">Flagship</div>
              </div>
              <div className="fleet-card-body">
                <div className="fleet-brand">
                  <ChevyLogo />
                </div>
                <h3>Chevrolet Suburban</h3>
                <div className="fleet-type">Full-Size Luxury SUV</div>
                <p>
                  The iconic black Suburban — commanding presence, spacious interior,
                  and the ultimate in comfort for executive travel and group transportation.
                </p>
                <div className="fleet-specs">
                  <div className="fleet-spec">
                    <span className="fleet-spec-label">Passengers</span>
                    <span className="fleet-spec-value">Up to 7</span>
                  </div>
                  <div className="fleet-spec">
                    <span className="fleet-spec-label">Luggage</span>
                    <span className="fleet-spec-value">Large</span>
                  </div>
                  <div className="fleet-spec">
                    <span className="fleet-spec-label">Color</span>
                    <span className="fleet-spec-value">Black</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="fleet-card">
              <div className="fleet-card-image">
                <img
                  src="/images/cadillac-lyriq.jpg"
                  alt="Black Cadillac Lyriq luxury electric SUV"
                  loading="lazy"
                />
                <div className="fleet-tag">Electric</div>
              </div>
              <div className="fleet-card-body">
                <div className="fleet-brand">
                  <CadillacLogo />
                </div>
                <h3>Cadillac Lyriq</h3>
                <div className="fleet-type">Luxury Electric SUV</div>
                <p>
                  Cutting-edge luxury meets sustainability. The Lyriq delivers a whisper-quiet,
                  ultra-smooth ride with futuristic technology and stunning design.
                </p>
                <div className="fleet-specs">
                  <div className="fleet-spec">
                    <span className="fleet-spec-label">Passengers</span>
                    <span className="fleet-spec-value">Up to 5</span>
                  </div>
                  <div className="fleet-spec">
                    <span className="fleet-spec-label">Drive</span>
                    <span className="fleet-spec-value">Electric</span>
                  </div>
                  <div className="fleet-spec">
                    <span className="fleet-spec-label">Color</span>
                    <span className="fleet-spec-value">Black</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: 48 }}>
            <Link to="/fleet" className="btn-outline">
              View Full Fleet <ArrowIcon />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="section">
        <div className="container">
          <div className="section-label">What We Offer</div>
          <h2 className="section-title">Our Services</h2>
          <p className="section-subtitle">
            From airport pickups to grand celebrations, we deliver excellence on every journey.
          </p>
          <div className="services-grid">
            {services.map((s, i) => (
              <div className="service-card" key={i}>
                <div className="service-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 48 }}>
            <Link to="/services" className="btn-primary">
              All Services <ArrowIcon />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="section-label">Client Reviews</div>
          <h2 className="section-title">What Our Clients Say</h2>
          <div className="testimonials-grid">
            {testimonials.map((t, i) => (
              <div className="testimonial-card" key={i}>
                <div className="quote-mark">{'\u201C'}</div>
                <div className="testimonial-stars">
                  {[...Array(5)].map((_, j) => <StarIcon key={j} />)}
                </div>
                <p>{t.text}</p>
                <div className="testimonial-author">{t.author}</div>
                <div className="testimonial-role">{t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="cta-banner">
        <div className="container">
          <h2>Ready for a <em>Premium</em> Experience?</h2>
          <p>Book your luxury black car today. Available 24/7 across greater Los Angeles.</p>
          <div className="cta-actions">
            <a href="tel:8189154566" className="btn-primary">
              <PhoneIcon /> (818) 915-4566
            </a>
            <Link to="/contact" className="btn-outline">
              Contact Us <ArrowIcon />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
